/**
 * Member API.
 *
 * BE endpoints:
 *   GET  /api/v1/user/members/me
 *   PUT  /api/v1/user/members/me
 *   POST /api/v1/user/members/me/profile-image  (multipart/form-data)
 */
import { apiGet, apiPut } from "./client";
import { uploadMultipart } from "./upload";

export interface Me {
  memberSerialNo: number;
  email: string;
  name: string | null;
  nickname: string | null;
  profileImageUrl: string | null;
  introduction: string | null;
  provider: "google" | "apple" | null;
}

export interface UpdateProfileRequest {
  nickname?: string;
  profileImageUrl?: string;
  /** 빈 문자열 또는 null 을 보내면 BE 에서 클리어. undefined 면 미갱신. */
  introduction?: string | null;
}

export async function fetchMe(): Promise<Me | null> {
  return apiGet<Me | null>("/members/me");
}

/**
 * 내 정보 알림 토글 4종 + quiet hours 시각.
 *
 * Y/N 값은 BE 와 동일한 표현 유지 ('Y' | 'N'). FE 는 boolean ↔ 'Y'/'N' 변환을
 * page level 에서 처리 (api 레이어는 wire format 그대로).
 */
export interface NotificationSettings {
  pushAgreementYn: "Y" | "N";
  /** 쇼핑 알림(관심작품 오픈·신규 상품). 푸시 마스터 하위 토글. */
  interestNotiYn: "Y" | "N";
  /** 배송·선물 알림(배송 현황·선물 수령/반환). 푸시 마스터 하위 토글. */
  deliveryNotiYn: "Y" | "N";
  /** 혜택·소식 알림(이벤트·프로모션·쿠폰·자랑하기). 푸시 마스터 하위 토글. */
  newsNotiYn: "Y" | "N";
  /** 보관·포인트 알림(보관기간 만료·포인트 적립/환급). 푸시 마스터 하위 토글. (187_ALTER) */
  storagePointNotiYn: "Y" | "N";
  nightQuietUseYn: "Y" | "N";
  nightQuietStartHour: number; // 0..23
  nightQuietEndHour: number;   // 0..23
}

export type UpdateNotificationSettingsRequest = Partial<NotificationSettings>;

export async function fetchNotificationSettings(): Promise<NotificationSettings | null> {
  return apiGet<NotificationSettings | null>("/members/me/notification-settings");
}

export async function updateNotificationSettings(
  req: UpdateNotificationSettingsRequest
): Promise<NotificationSettings> {
  return apiPut<NotificationSettings>("/members/me/notification-settings", req);
}

export async function updateMyProfile(req: UpdateProfileRequest): Promise<Me> {
  return apiPut<Me>("/members/me", req);
}

/**
 * 프로필 사진 업로드 — multipart/form-data.
 * BE: POST /api/v1/user/members/me/profile-image
 *   → CNCM_ATCH_FILE 등록 + Member.PRFL_IMG_URL 갱신 → 최신 Me 반환.
 */
export async function uploadMyProfileImage(file: File): Promise<Me> {
  return uploadMultipart<Me>("/members/me/profile-image", file);
}
